import { message, Layout } from 'antd';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import WritePost from './pages/WritePost';
import EditPost from './pages/EditPost';
import Archive from './pages/Archive';
import Home from './pages/Home';
import Auth from './pages/Auth';
import * as authClient from './api/authClient';
import About from './pages/About';
import PostDetail from './pages/PostDetail';
import MyPosts from './pages/MyPosts';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import { samplePosts, sampleUsers, aboutInfo as mockAbout } from './api/mockData';
import FooterItem from './components/FooterItem';
import Header from './components/Header';
import './App.css';
// 定义本地存储的键名常量
const LOCAL_STORAGE_POSTS_KEY = 'blogPosts';
const LOCAL_STORAGE_USERS_KEY = 'blogUsers';
const LOCAL_STORAGE_CURRENT_USER_KEY = 'blogCurrentUser';

// 导出常量（供其他文件使用）
export {
  LOCAL_STORAGE_POSTS_KEY,
  LOCAL_STORAGE_USERS_KEY,
  LOCAL_STORAGE_CURRENT_USER_KEY,
};

function App() {
  // 状态
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null);
  // 搜索与分页（由各页面自行管理）

  // 写文章由单独页面管理，不在 App 中保存标题/内容

  // 登录名输入（由 Auth 页面管理）

  // optional about info (use mock by default)
  const [aboutInfoState] = useState(mockAbout || {});

  // 收集所有标签用于写文章/编辑时的建议
  const allTags = Array.from(new Set((posts || []).flatMap((p) => (Array.isArray(p.tags) ? p.tags : [])))).sort();

  // 初始化：从 localStorage 读取
  useEffect(() => {
    try {
      const postsRaw = localStorage.getItem(LOCAL_STORAGE_POSTS_KEY);
      const usersRaw = localStorage.getItem(LOCAL_STORAGE_USERS_KEY);
      const currentRaw = localStorage.getItem(LOCAL_STORAGE_CURRENT_USER_KEY);

      if (postsRaw) setPosts(JSON.parse(postsRaw));
      else if (samplePosts) setPosts(samplePosts);

      if (usersRaw) {
        try {
          setUsers(JSON.parse(usersRaw));
        } catch (e) {
          console.error('解析本地用户数据失败，已重置', e);
          localStorage.removeItem(LOCAL_STORAGE_USERS_KEY);
          setUsers(sampleUsers || []);
        }
      } else if (sampleUsers) setUsers(sampleUsers);

      if (currentRaw) setLoggedInUser(JSON.parse(currentRaw));

      setLoading(false);
    } catch (err) {
      console.error('初始化失败', err);
      message.error('无法读取本地存储');
      setLoading(false);
    }
  }, []);

  // 当 posts/users/currentUser 变化时保存到 localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_POSTS_KEY, JSON.stringify(posts));
    } catch (e) {
      console.error('保存文章失败', e);
    }
  }, [posts]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_USERS_KEY, JSON.stringify(users));
    } catch (e) {
      console.error('保存用户失败', e);
    }
  }, [users]);

  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_CURRENT_USER_KEY, JSON.stringify(loggedInUser));
    } catch (e) {
      console.error('保存当前用户失败', e);
    }
  }, [loggedInUser]);
  
    // 更新用户信息
  const handleUpdateUser = (updatedUser) => {
    // 更新用户列表中的用户信息
    const updatedUsers = users.map(user => 
      user.id === updatedUser.id ? updatedUser : user
    );
    setUsers(updatedUsers);
    
    // 如果是当前登录用户，更新登录状态
    if (loggedInUser && loggedInUser.id === updatedUser.id) {
      setLoggedInUser(updatedUser);
    }
    
    // 同步更新localStorage
    try {
      localStorage.setItem(LOCAL_STORAGE_USERS_KEY, JSON.stringify(updatedUsers));
      if (loggedInUser && loggedInUser.id === updatedUser.id) {
        localStorage.setItem(LOCAL_STORAGE_CURRENT_USER_KEY, JSON.stringify(updatedUser));
      }
    } catch (e) {
      console.error('保存用户数据失败', e);
    }
  };
  // （App 不直接处理行内登录，使用 Auth 页面提供的回调）

  function handleLogout() {
    setLoggedInUser(null);
    message.info('已登出');
  }

  // 登录辅助：按名称登录或注册（供 Auth 页面回调使用）
  function loginByName(name) {
    const n = name && name.trim();
    if (!n) {
      message.error('用户名不能为空');
      return;
    }
    let user = users.find((u) => u.name === n);
    if (!user) {
      user = { id: uuidv4(), name: n };
      const newUsers = [...users, user];
      setUsers(newUsers);
      try {
        localStorage.setItem(LOCAL_STORAGE_USERS_KEY, JSON.stringify(newUsers));
      } catch (e) {
        console.error('保存用户失败', e);
      }
      message.success(`已创建用户 ${n} 并登录`);
    } else {
      message.success(`已登录：${n}`);
    }
    setLoggedInUser(user);
    try {
      localStorage.setItem(LOCAL_STORAGE_CURRENT_USER_KEY, JSON.stringify(user));
    } catch (e) {
      console.error('保存当前用户失败', e);
    }
  }

  // 密码哈希（浏览器端，使用 SubtleCrypto）
  async function hashPassword(password) {
    try {
      const enc = new TextEncoder();
      const data = enc.encode(password);
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
      // Fallback to base64 encoding (不安全，仅演示用途)
      return btoa(password);
    }
  }

  // 注册：保存带密码哈希的用户
  async function handleRegister(username, password) {
    const name = username && username.trim();
    if (!name || !password) {
      return { success: false, message: '用户名和密码不能为空' };
    }
    const exists = users.find((u) => u.name === name);
    if (exists) {
      return { success: false, fieldErrors: { username: '用户名已存在' } };
    }
    const ph = await hashPassword(password);
    const user = { id: uuidv4(), name, passwordHash: ph };
    const newUsers = [...users, user];
    setUsers(newUsers);
    try {
      localStorage.setItem(LOCAL_STORAGE_USERS_KEY, JSON.stringify(newUsers));
    } catch (e) {
      console.error('保存用户失败', e);
    }
    setLoggedInUser(user);
    try {
      localStorage.setItem(LOCAL_STORAGE_CURRENT_USER_KEY, JSON.stringify(user));
      // save plain credentials locally for autofill (demo use only)
      try { localStorage.setItem('savedCredentials', JSON.stringify({ username: name, password })); } catch (e) { /* ignore */ }
    } catch (e) {
      console.error('保存当前用户失败', e);
    }
    return { success: true, user };
  }

  // 使用用户名/密码登录
  async function handleLoginCredentials(username, password) {
    const name = username && username.trim();
    if (!name || !password) {
      return { success: false, message: '请输入用户名和密码' };
    }
    const user = users.find((u) => u.name === name);
    if (!user) {
      return { success: false, fieldErrors: { username: '用户不存在' }, message: '用户不存在，请先注册' };
    }
    const ph = await hashPassword(password);
    if (ph !== user.passwordHash) {
      return { success: false, message: '用户名或密码错误', fieldErrors: { password: '密码不正确' } };
    }
    setLoggedInUser(user);
    try { localStorage.setItem('savedCredentials', JSON.stringify({ username: name, password })); } catch (e) { /* ignore */ }
    return { success: true, user };
  }

  // 清除 localStorage 并退出登录（用于 demo）
  function clearLocalData() {
    try {
      localStorage.removeItem(LOCAL_STORAGE_POSTS_KEY);
      localStorage.removeItem(LOCAL_STORAGE_USERS_KEY);
      localStorage.removeItem(LOCAL_STORAGE_CURRENT_USER_KEY);
      setPosts([]);
      setUsers([]);
      setLoggedInUser(null);
      message.info('本地数据已清除并已登出');
    } catch (e) {
      console.error('清除本地数据失败', e);
      message.error('清除本地数据失败');
    }
  }

  // 导出本地数据（posts + users）
  function handleExportData() {
    try {
      const data = { posts, users };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blog-backup-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success('已导出本地数据');
    } catch (e) {
      console.error('导出失败', e);
      message.error('导出失败');
    }
  }

  // 新增文章（供 WritePost 页面调用）
  // 现在支持 summary 与 tags 字段
  function handleAddPost(title, summary, tags = [], content) {
    if (!loggedInUser) {
      message.error('请先登录再发表文章');
      return false;
    }
    const t = title && title.trim();
    const c = content && content.trim();
    if (!t || !c) {
      message.error('标题和内容不能为空');
      return false;
    }
    const post = {
      id: uuidv4(),
      title: t,
      summary: summary && summary.trim(),
      tags: Array.isArray(tags) ? tags : [],
      content: c,
      readBy: [],
      authorId: loggedInUser.id,
      authorName: loggedInUser.name,
      createdAt: Date.now(),
    };
    setPosts((prev) => [post, ...prev]);
    message.success('文章已发布');
    return true;
  }

  // 编辑文章（现在支持 summary 与 tags）
  function handleEditPost(postId, title, summary, tags = [], content) {
    const t = title && title.trim();
    const c = content && content.trim();
    if (!t || !c) {
      message.error('标题和内容不能为空');
      return false;
    }
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, title: t, summary: summary && summary.trim(), tags: Array.isArray(tags) ? tags : [], content: c } : p)));
    message.success('文章已更新');
    return true;
  }

  // 标记文章为已读（为当前用户）
  function markPostRead(postId, userId) {
    if (!postId || !userId) return;
    setPosts((prev) =>
      prev.map((p) => {
        if (p.id !== postId) return p;
        const readBy = Array.isArray(p.readBy) ? p.readBy.slice() : [];
        if (!readBy.includes(userId)) {
          readBy.push(userId);
        }
        return { ...p, readBy };
      })
    );
  }

  function handleDeletePost(postId) {
    const post = posts.find((p) => p.id === postId);
    if (!post) return;
    if (!loggedInUser || post.authorId !== loggedInUser.id) {
      message.error('只能删除自己发布的文章');
      return;
    }
    setPosts((prev) => prev.filter((p) => p.id !== postId));
    message.success('文章已删除');
  }

  if (loading) {
    return (
      <div className="App">
        <header className="App-header">
          <p>加载中…</p>
        </header>
      </div>
    );
  }

  return (
    <Router>
        <Layout className="App" style={{ minHeight: '100vh', backgroundImage: loggedInUser?.background ? `url(${loggedInUser.background})` : 'none', backgroundSize: 'cover' }}>
        <Layout.Header style={{ display: 'flex', alignItems: 'center', gap: 16, backgroundColor: loggedInUser?.background ? 'rgba(0,0,0,0.18)' : '#0f1724', backdropFilter: 'blur(4px)' }}>
          {/* 统计数据：总文章数 / 我的文章数 / 未读数（不含自己的文章） */}
          <Header
            loggedInUser={loggedInUser}
            onLogout={handleLogout}
            unreadCount={loggedInUser ? posts.filter((p) => !(Array.isArray(p.readBy) ? p.readBy.includes(loggedInUser.id) : false) && p.authorId !== loggedInUser.id).length : 0}
          />
        </Layout.Header>

        <Layout.Content className="app-content" style={{ padding: '24px', backgroundColor: loggedInUser?.background ? 'rgba(255,255,255,0.65)' : undefined }}>
          <Routes>
            <Route path="/" element={<Home posts={posts} loggedInUser={loggedInUser} onDelete={handleDeletePost} />} />

            <Route
              path="/auth"
              element={(
                <Auth
                  onLogin={async (vals) => {
                    if (!vals) return;
                    if (typeof vals === 'string') return loginByName(vals);
                    // 如果设置了后端代理，优先使用后端（仅在显式开启时）
                    if (window.__USE_BACKEND__) {
                      // try backend login via authClient
                      if (vals.username && vals.password) {
                        try {
                          const res = await authClient.login(vals);
                          if (res && res.success && res.user) {
                            // ensure user exists locally for mapping
                            const u = users.find((x) => x.name === res.user.username);
                            if (!u) {
                              const newUsers = [...users, { id: res.user.id, name: res.user.username }];
                              setUsers(newUsers);
                              try {
                                localStorage.setItem(LOCAL_STORAGE_USERS_KEY, JSON.stringify(newUsers));
                              } catch (e) {
                                console.error('保存用户失败', e);
                              }
                            }
                            const newCurrent = { id: res.user.id, name: res.user.username, token: res.token };
                            setLoggedInUser(newCurrent);
                            // store token if present
                            if (res.token) localStorage.setItem('authToken', res.token);
                            try {
                              localStorage.setItem(LOCAL_STORAGE_CURRENT_USER_KEY, JSON.stringify(newCurrent));
                            } catch (e) {
                              console.error('保存当前用户失败', e);
                            }
                            return { success: true, user: res.user };
                          }
                          return { success: false, message: res && res.message };
                        } catch (e) {
                          console.error('backend login failed', e);
                          // fallback to local handler
                        }
                      }
                    }
                    // fallback to local credential handler
                    if (vals.username && vals.password) return await handleLoginCredentials(vals.username, vals.password);
                    if (vals.username) return loginByName(vals.username);
                    return false;
                  }}
                  onRegister={async (vals) => {
                    if (!vals) return false;
                    if (window.__USE_BACKEND__) {
                      if (vals.username && vals.password) {
                        try {
                          const res = await authClient.register(vals);
                          if (res && res.success && res.user) {
                            // ensure local mapping
                            const u = users.find((x) => x.name === res.user.username);
                            if (!u) {
                              const newUsers = [...users, { id: res.user.id, name: res.user.username }];
                              setUsers(newUsers);
                              try {
                                localStorage.setItem(LOCAL_STORAGE_USERS_KEY, JSON.stringify(newUsers));
                              } catch (e) {
                                console.error('保存用户失败', e);
                              }
                            }
                            const newCurrent = { id: res.user.id, name: res.user.username, token: res.token };
                            setLoggedInUser(newCurrent);
                            if (res.token) localStorage.setItem('authToken', res.token);
                            try {
                              localStorage.setItem(LOCAL_STORAGE_CURRENT_USER_KEY, JSON.stringify(newCurrent));
                            } catch (e) {
                              console.error('保存当前用户失败', e);
                            }
                            return { success: true, user: res.user };
                          }
                          return { success: false, message: res && res.message };
                        } catch (e) {
                          console.error('backend register failed', e);
                        }
                      }
                    }
                    if (vals.username && vals.password) return await handleRegister(vals.username, vals.password);
                    return false;
                  }}
                />
              )}
            />

            <Route path="/post/:id" element={<PostDetail posts={posts} loggedInUser={loggedInUser} markPostRead={markPostRead} />} />

            <Route path="/archive" element={<Archive posts={posts} />} />

            <Route path="/about" element={<About aboutInfo={aboutInfoState} />} />

            <Route path="/write" element={<WritePost onAddPost={handleAddPost} loggedInUser={loggedInUser} allTags={allTags} />} />

            <Route path="/edit/:id" element={<EditPost posts={posts} onEditPost={handleEditPost} loggedInUser={loggedInUser} allTags={allTags} />} />
            
            <Route path="/myposts" element={<MyPosts posts={posts} loggedInUser={loggedInUser} onDelete={handleDeletePost} onUpdateUser={handleUpdateUser} />} />
            <Route path="/settings" element={<Settings onClearData={clearLocalData} onExportData={handleExportData} loggedInUser={loggedInUser} />} />
            
            {/* catch-all 404 route */}
            <Route path="*" element={<NotFound loggedInUser={loggedInUser} onLogout={handleLogout} />} />
          </Routes>
        </Layout.Content>
        <FooterItem links={[{ href: 'https://github.com', label: 'GitHub' }]} />
      </Layout>
    </Router>
  );
}


// 文章列表组件（带搜索、分页、预览、编辑按钮）
// ArticleList and PaginationSimple removed — list/pagination handled per-page now.

export default App;
